/**
 * The settings of one sidebar card: the whole automation (trigger, Hub,
 * history) or one step. Beside a docked sidebar it slides out to the left of
 * the sidebar, as wide as the sidebar's default width, so the canvas stays
 * visible (a click on it closes the pane). Without a host (floating sidebar,
 * phones) it slides over the sidebar from the left.
 *
 * The sidebar's cards are the navigation: the pane has no list of its own.
 * It shows the card's settings in use; optional settings that are not used
 * yet wait at the end as "+" buttons and open in place. A setting switched
 * off in place (status tracking) goes back there. The requested setting
 * is scrolled into view and highlighted; the setting of an open item the
 * user asked for also sends the sidebar's attention pulse. Run, Activate and
 * the status stay in the sidebar's footer.
 */
import { Tooltip } from '@affine/component';
import { useI18n } from '@affine/i18n';
import { streamLainAnimationController } from '@blocksuite/affine-shared/utils';
import { CloseIcon, PlusIcon } from '@blocksuite/icons/rc';
import {
  type FC,
  type ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';

import { VIEW_PANE_INLINE_END_PROPERTY } from '../shared/view-pane-inset';
import type {
  AutomationReadiness,
  ReadinessTileId,
} from './automation-readiness';
import * as styles from './automation-settings-panel.css';
import {
  ConcealSettingContext,
  HiddenSettingsContext,
  SettingAttentionContext,
  SettingStatesContext,
} from './components/setting-section';
import { settingProgress, useSettingProgressLabel } from './setting-progress';
import {
  PANE_PRIMARY_TILE,
  paneOfTile,
  type SettingsPane,
  tilesOfPane,
} from './settings-pane';

export interface AutomationSettingsPanelProps {
  open?: boolean;
  /** The automation's name, for assistive technology. */
  title: string;
  /** The setting to show; its card's pane is the content. */
  focusTile: ReadinessTileId;
  readiness: Pick<AutomationReadiness, 'tiles' | 'issues'>;
  /** The settings of each pane, built from `SettingSection`s. */
  groups: Record<SettingsPane, ReactNode>;
  /** The icon of each pane, as on its sidebar card. */
  icons?: Partial<Record<SettingsPane, ReactNode>>;
  onClose: () => void;
  /** The active view's body beside the docked sidebar; null covers the screen. */
  host?: HTMLElement | null;
  /** An unused setting was added (e.g. status tracking switches on). */
  onReveal?: (tile: ReadinessTileId) => void;
  /** The setting to point out with the attention pulse. */
  attentionTile?: ReadinessTileId | null;
  /** The pulse has ended. */
  onAttentionEnd?: () => void;
}

const VIEW_PANE_HIDDEN: Keyframe = {
  opacity: 0,
  transform: 'translateX(100%)',
};
const VIEW_PANE_RESTING: Keyframe = { opacity: 1, transform: 'translateX(0)' };
/** Over a floating sidebar the pane slides in from the left. */
const SCREEN_PANE_HIDDEN: Keyframe = {
  opacity: 0,
  transform: 'translateX(-100%)',
};
/** Another card's settings come in from below. */
const PANE_CONTENT_ENTER: Keyframe[] = [
  { opacity: 0, transform: 'translateY(12px)' },
  { opacity: 1, transform: 'translateY(0)' },
];

export const AutomationSettingsPanel: FC<AutomationSettingsPanelProps> = ({
  open = true,
  title,
  focusTile,
  readiness,
  groups,
  icons = {},
  onClose,
  host = null,
  onReveal,
  attentionTile = null,
  onAttentionEnd,
}) => {
  const t = useI18n();
  const progressLabel = useSettingProgressLabel();
  const panelRef = useRef<HTMLElement>(null);
  const [present, setPresent] = useState(open);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const previousOpen = useRef(false);
  const formRef = useRef<HTMLDivElement>(null);
  const [selected, setSelected] = useState<ReadinessTileId>(focusTile);
  const [scrollRequest, setScrollRequest] = useState(open ? 1 : 0);
  const [revealed, setRevealed] = useState<ReadonlySet<ReadinessTileId>>(
    () => new Set()
  );
  // Settings switched off here wait among the unused ones again, even the
  // one the pane opened at.
  const [concealed, setConcealed] = useState<ReadonlySet<ReadinessTileId>>(
    () => new Set()
  );
  const activePane = paneOfTile(selected);
  const paneTiles = tilesOfPane(activePane);
  // The general pane always shows its few settings.
  const unusedTiles =
    activePane === 'general'
      ? []
      : paneTiles.filter(
          tile =>
            readiness.tiles[tile] === 'none' &&
            (concealed.has(tile) || (tile !== selected && !revealed.has(tile)))
        );
  const hiddenTiles = new Set<ReadinessTileId>(unusedTiles);

  const select = useCallback((tile: ReadinessTileId) => {
    setSelected(tile);
    setScrollRequest(request => request + 1);
  }, []);

  // The requested setting applies in the render that opens the pane, so the
  // pane slides in with its content instead of swapping it on the way.
  const [request, setRequest] = useState({ open, focusTile });
  if (request.open !== open || request.focusTile !== focusTile) {
    setRequest({ open, focusTile });
    if (open) {
      setRevealed(new Set());
      setConcealed(new Set());
      select(focusTile);
    }
  }

  useLayoutEffect(() => {
    if (open && !present) {
      setPresent(true);
      return;
    }
    const panel = panelRef.current;
    if (!panel) return;
    panel.inert = !open;
    if (open) {
      if (!previousOpen.current)
        returnFocusRef.current = document.activeElement as HTMLElement | null;
      previousOpen.current = true;
      streamLainAnimationController.play(
        panel,
        host
          ? [VIEW_PANE_HIDDEN, VIEW_PANE_RESTING]
          : [SCREEN_PANE_HIDDEN, VIEW_PANE_RESTING],
        host ? 'page' : 'enter'
      );
      panel
        .querySelector<HTMLButtonElement>('[data-panel-close]')
        ?.focus({ preventScroll: true });
    } else if (previousOpen.current) {
      previousOpen.current = false;
      returnFocusRef.current?.focus({ preventScroll: true });
      void streamLainAnimationController
        .play(
          panel,
          host
            ? [VIEW_PANE_RESTING, VIEW_PANE_HIDDEN]
            : [VIEW_PANE_RESTING, SCREEN_PANE_HIDDEN],
          host ? 'pageExit' : 'exit'
        )
        .finished.then(outcome => {
          if (outcome === 'finished') setPresent(false);
        });
    }
    return () => streamLainAnimationController.cancel(panel);
  }, [host, open, present]);

  // Switching cards while open brings the other card's settings in.
  const sectionsRef = useRef<HTMLDivElement>(null);
  const shownPane = useRef<SettingsPane | null>(null);
  useLayoutEffect(() => {
    const sections = sectionsRef.current;
    const previous = shownPane.current;
    shownPane.current = open ? activePane : null;
    if (!open || !sections || previous === null || previous === activePane)
      return;
    streamLainAnimationController.play(sections, PANE_CONTENT_ENTER, 'enter');
    return () => streamLainAnimationController.cancel(sections);
  }, [activePane, open]);

  // One controller owns the panel, section feedback and scrolling.
  useLayoutEffect(() => {
    if (!open || scrollRequest === 0) return;
    const form = formRef.current;
    const section = formRef.current?.querySelector<HTMLElement>(
      `[data-setting="${selected}"]`
    );
    if (!form || !section) return;
    const top =
      form.scrollTop +
      section.getBoundingClientRect().top -
      form.getBoundingClientRect().top -
      16;
    streamLainAnimationController.scrollTo(form, {
      top: Math.max(0, Math.min(top, form.scrollHeight - form.clientHeight)),
    });
    // The setting a card opens at is the pane's top: nothing to point out.
    if (selected === PANE_PRIMARY_TILE[paneOfTile(selected)]) {
      return () => streamLainAnimationController.cancel(form);
    }
    streamLainAnimationController.play(
      section,
      [
        {
          outline: '2px solid var(--streamlain-v2-button-primary)',
          outlineOffset: '2px',
        },
        { outline: '2px solid transparent', outlineOffset: '2px' },
      ],
      'page'
    );
    return () => {
      streamLainAnimationController.cancel(form);
      streamLainAnimationController.cancel(section);
    };
  }, [open, present, scrollRequest, selected]);

  // The view's bottom row (context dock) keeps clear of the open pane.
  useLayoutEffect(() => {
    const panel = panelRef.current;
    if (!host || !open || !present || !panel) return;
    const body = panel.ownerDocument.body;
    const publish = () =>
      body.style.setProperty(
        VIEW_PANE_INLINE_END_PROPERTY,
        `${panel.offsetWidth}px`
      );
    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(panel);
    return () => {
      observer.disconnect();
      body.style.removeProperty(VIEW_PANE_INLINE_END_PROPERTY);
    };
  }, [host, open, present]);

  useEffect(() => {
    const onKeyDown = (event: globalThis.KeyboardEvent) => {
      if (open && event.key === 'Escape' && !event.defaultPrevented) onClose();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [onClose, open]);

  const attention = useMemo(
    () => ({ tile: attentionTile, onEnd: onAttentionEnd }),
    [attentionTile, onAttentionEnd]
  );

  const reveal = (tile: ReadinessTileId) => {
    setRevealed(current => new Set(current).add(tile));
    setConcealed(current => {
      const next = new Set(current);
      next.delete(tile);
      return next;
    });
    onReveal?.(tile);
    select(tile);
  };

  const conceal = useCallback((tile: ReadinessTileId) => {
    setRevealed(current => {
      const next = new Set(current);
      next.delete(tile);
      return next;
    });
    setConcealed(current => new Set(current).add(tile));
  }, []);

  const paneLabel = t.t(`com.streamlain.automation.panel.group.${activePane}`);
  const headingId = `automation-settings-${activePane}`;
  const progress =
    activePane === 'general'
      ? null
      : settingProgress(paneTiles, readiness.tiles);

  if (!present) return null;
  return createPortal(
    <>
      {host && open && (
        <button
          type="button"
          className={styles.canvasStrip}
          onClick={onClose}
          tabIndex={-1}
          aria-label={t['com.streamlain.automation.panel.close']()}
        />
      )}
      <aside
        ref={panelRef}
        className={styles.panel}
        data-placement={host ? 'view' : 'screen'}
        data-settings-group={activePane}
        role="dialog"
        aria-hidden={!open || undefined}
        data-state={open ? 'open' : 'closed'}
        aria-labelledby={headingId}
        aria-description={title}
        data-testid="automation-settings-panel"
      >
        <header className={styles.header}>
          {icons[activePane] && (
            <span className={styles.headerIcon} aria-hidden="true">
              {icons[activePane]}
            </span>
          )}
          <div className={styles.headerText}>
            <h2 id={headingId} className={styles.headerTitle}>
              {paneLabel}
            </h2>
            {progress && progress.total > 0 && (
              <p
                className={styles.headerProgress}
                data-open={progress.open > 0 || undefined}
                data-testid="automation-settings-progress"
              >
                {progressLabel(progress)}
              </p>
            )}
          </div>
          <button
            type="button"
            className={styles.iconButton}
            data-panel-close
            onClick={onClose}
            aria-label={t['com.streamlain.automation.panel.close']()}
            title={t['com.streamlain.automation.panel.close']()}
          >
            <CloseIcon width={20} height={20} />
          </button>
        </header>

        <div ref={formRef} className={styles.form}>
          <div
            key={activePane}
            ref={sectionsRef}
            className={styles.formSections}
          >
            <SettingStatesContext.Provider value={readiness.tiles}>
              <HiddenSettingsContext.Provider value={hiddenTiles}>
                <ConcealSettingContext.Provider value={conceal}>
                  <SettingAttentionContext.Provider value={attention}>
                    {groups[activePane]}
                  </SettingAttentionContext.Provider>
                </ConcealSettingContext.Provider>
              </HiddenSettingsContext.Provider>
            </SettingStatesContext.Provider>
          </div>

          {unusedTiles.length > 0 && (
            <section
              className={styles.moreSettings}
              aria-labelledby={`${headingId}-more`}
            >
              <h3 id={`${headingId}-more`} className={styles.moreTitle}>
                {t['com.streamlain.automation.panel.more-settings']()}
              </h3>
              <ul className={styles.moreList}>
                {unusedTiles.map(tile => (
                  <li key={tile}>
                    <Tooltip
                      content={t.t(
                        `com.streamlain.automation.panel.help.${tile}`
                      )}
                    >
                      <button
                        type="button"
                        className={styles.moreButton}
                        data-tile={tile}
                        onClick={() => reveal(tile)}
                        aria-description={t.t(
                          `com.streamlain.automation.panel.help.${tile}`
                        )}
                      >
                        <PlusIcon aria-hidden="true" />
                        <span className={styles.moreName}>
                          {t.t(`com.streamlain.automation.panel.tile.${tile}`)}
                        </span>
                      </button>
                    </Tooltip>
                  </li>
                ))}
              </ul>
            </section>
          )}
        </div>
      </aside>
    </>,
    host ?? document.body
  );
};
